import React from "react";
import { ChevronRight, ChevronLeft, FileText } from "lucide-react";
import { useImportStore } from "../../store/useImportStore";
import { formatFileSize } from "../../utils/formatters";

export const DataPreview: React.FC = () => {
  const { currentFile, preview, setCurrentStep } = useImportStore();

  if (!preview || !currentFile) {
    return <div className="p-8">No preview data available</div>;
  }

  const handleNext = () => {
    setCurrentStep("mapping");
  };

  const handleBack = () => {
    setCurrentStep("upload");
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Preview Your Data
        </h2>
        <p className="text-gray-600">
          Review your CSV file structure and verify the data looks correct
          before proceeding.
        </p>
      </div>

      {/* File Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-4">
          <FileText className="w-8 h-8 text-blue-500" />
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">{currentFile.name}</h3>
            <div className="text-sm text-gray-600 space-x-4">
              <span>Size: {formatFileSize(currentFile.size)}</span>
              <span>Rows: {preview.total_rows.toLocaleString()}</span>
              <span>Columns: {preview.headers.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detected Format */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">Detected Format</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Delimiter:</span>
            <span className="ml-2 font-mono bg-white px-1 rounded">
              {preview.detected_format.delimiter === "\t"
                ? "Tab"
                : preview.detected_format.delimiter}
            </span>
          </div>
          <div>
            <span className="text-blue-700">Header Row:</span>
            <span className="ml-2">
              {preview.detected_format.has_header ? "Yes" : "No"}
            </span>
          </div>
          <div>
            <span className="text-blue-700">Encoding:</span>
            <span className="ml-2">
              {preview.detected_format.encoding || "UTF-8"}
            </span>
          </div>
          <div>
            <span className="text-blue-700">Skip Rows:</span>
            <span className="ml-2">
              {preview.detected_format.skip_rows || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Data Preview Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">
            Sample Data (First 10 rows)
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Row
                </th>
                {preview.headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {preview.sample_rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-4 py-2 text-sm text-gray-500 font-mono">
                    {rowIndex + 1}
                  </td>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate"
                    >
                      <span title={String(cell)}>{String(cell)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suggested Mappings Preview */}
      <div className="bg-green-50 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-green-900 mb-2">
          Suggested Column Mappings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
          {preview.suggested_mappings
            .filter((mapping) => mapping.target_field !== "ignore")
            .map((mapping, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="text-green-700">{mapping.csv_column}</span>
                <ChevronRight className="w-3 h-3 text-green-600" />
                <span className="text-green-900 font-medium">
                  {mapping.target_field}
                </span>
              </div>
            ))}
        </div>
        <p className="text-xs text-green-700 mt-2">
          These mappings are automatically suggested. You can modify them in the
          next step.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <button
          onClick={handleNext}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
        >
          <span>Configure Mapping</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
